import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../services/api';

function FAQs() {
  const [showForm, setShowForm] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    question: '',
    answer: '',
    display_order: 0,
    is_active: true,
  });
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchFAQs();
  }, [filterCategory]);

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const params = filterCategory ? { category: filterCategory } : {};
      const response = await notificationsAPI.getFAQs(params);
      const faqData = response.data.data || [];
      setFaqs(faqData);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(faqData.map(faq => faq.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'display_order' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category.trim() || !formData.question.trim() || !formData.answer.trim()) {
      setMessage({ type: 'error', text: 'Category, question, and answer are required' });
      return;
    }

    try {
      if (editingFaqId) {
        await notificationsAPI.updateFAQ(editingFaqId, formData);
        setMessage({ type: 'success', text: 'FAQ updated successfully!' });
      } else {
        await notificationsAPI.createFAQ(formData);
        setMessage({ type: 'success', text: 'FAQ created successfully!' });
      }

      setFormData({
        category: '',
        question: '',
        answer: '',
        display_order: 0,
        is_active: true,
      });
      setEditingFaqId(null);
      
      setShowForm(false);
      fetchFAQs();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save FAQ' 
      });
    }
  };

  const handleEditFAQ = (faq) => {
    setEditingFaqId(faq.id);
    setFormData({
      category: faq.category || '',
      question: faq.question || '',
      answer: faq.answer || '',
      display_order: faq.display_order || 0,
      is_active: Boolean(faq.is_active),
    });
    setShowForm(true);
    setMessage({ type: '', text: '' });
  };

  const handleDeleteFAQ = async (faqId) => {
    if (!window.confirm('정말 삭제하시겠습니까? 삭제하면 복구할 수 없습니다.')) {
      return;
    }

    try {
      await notificationsAPI.deleteFAQ(faqId);
      setMessage({ type: 'success', text: 'FAQ deleted successfully!' });
      fetchFAQs();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete FAQ' 
      });
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingFaqId(null);
    setFormData({
      category: '',
      question: '',
      answer: '',
      display_order: 0,
      is_active: true,
    });
  };

  // Group FAQs by category
  const groupedFAQs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h1>FAQ 관리</h1>
        <p> FAQ 관리 </p>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            if (showForm) {
              cancelForm();
            } else {
              setEditingFaqId(null);
              setFormData({
                category: '',
                question: '',
                answer: '',
                display_order: 0,
                is_active: true,
              });
              setShowForm(true);
            }
          }}
          style={{ marginTop: '10px' }}
        >
          {showForm ? 'Cancel' : '+ Create FAQ'}
        </button>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>{editingFaqId ? 'Edit FAQ' : 'Create New FAQ'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  name="category"
                  className="form-control"
                  placeholder="e.g., General, Account, Device"
                  value={formData.category}
                  onChange={handleInputChange}
                  list="category-suggestions"
                  required
                />
                <datalist id="category-suggestions">
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="display_order"
                  className="form-control"
                  min="0"
                  value={formData.display_order}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Question *</label>
              <input
                type="text"
                name="question"
                className="form-control"
                placeholder="What is your question?"
                value={formData.question}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Answer *</label>
              <textarea
                name="answer"
                className="form-control"
                placeholder="Detailed answer to the question"
                value={formData.answer}
                onChange={handleInputChange}
                rows="5"
                required
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (show to users)
              </label>
            </div>

            <button type="submit" className="btn btn-success">
              {editingFaqId ? 'Update FAQ' : 'Create FAQ'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All FAQs</h2>
          <select
            className="form-control"
            style={{ width: '200px' }}
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div className="loading">Loading FAQs...</div>
        ) : faqs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No FAQs created yet
          </div>
        ) : (
          <div>
            {Object.entries(groupedFAQs).map(([category, categoryFAQs]) => (
              <div key={category} style={{ marginBottom: '30px' }}>
                <h3 style={{ 
                  padding: '10px 20px', 
                  background: '#ecf0f1', 
                  borderRadius: '4px',
                  marginBottom: '15px' 
                }}>
                  {category}
                </h3>
                
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Question</th>
                        <th>Answer</th>
                      <th>Status</th>
                      <th>Views</th>
                      <th>Helpful</th>
                      <th>Not Helpful</th>
                      <th>Helpfulness</th>
                      <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                      {categoryFAQs.map((faq) => {
                        const totalVotes = faq.helpful_count + faq.not_helpful_count;
                        const helpfulRate = totalVotes > 0 
                          ? Math.round((faq.helpful_count / totalVotes) * 100) 
                          : 0;
                        
                        return (
                          <tr key={faq.id}>
                            <td>{faq.display_order}</td>
                            <td style={{ maxWidth: '300px' }}>{faq.question}</td>
                            <td style={{ maxWidth: '400px', fontSize: '12px', color: '#7f8c8d' }}>
                              {faq.answer.substring(0, 100)}...
                            </td>
                            <td>
                              <span className={`badge ${faq.is_active ? 'badge-success' : 'badge-danger'}`}>
                                {faq.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>{faq.view_count}</td>
                            <td>{faq.helpful_count}</td>
                            <td>{faq.not_helpful_count}</td>
                            <td>
                              {totalVotes > 0 ? (
                                <span className={`badge ${helpfulRate >= 70 ? 'badge-success' : helpfulRate >= 40 ? 'badge-warning' : 'badge-danger'}`}>
                                  {helpfulRate}%
                            </span>
                          ) : (
                            <span className="badge badge-info">No votes</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => handleEditFAQ(faq)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => handleDeleteFAQ(faq.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FAQs;
